import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import systemSettingsService from '../../../services/systemSettingsService';
import { setCurrency } from '../../../redux/slices/authSlice';
import { formatMoney } from '../../../hooks/useCurrency';

// Mirrors SystemSettingsService.SUPPORTED_CURRENCIES on the backend (which is authoritative:
// it derives the saved symbol from the ISO code, so a tampered request can't store another one).
const CURRENCIES = [
  { code: 'DZD', symbol: 'DA' },
  { code: 'EUR', symbol: '€' },
  { code: 'USD', symbol: '$' },
];

const PREVIEW_AMOUNT = 1000;

const SystemSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isSuperAdmin = user?.globalRole === 'SUPER_ADMIN';

  const [currency, setCurrencyCode] = useState('DZD');
  const [position, setPosition] = useState('AFTER');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    systemSettingsService
      .getSettings()
      .then((settings) => {
        if (cancelled) return;
        setCurrencyCode(settings.currency || 'DZD');
        setPosition(settings.currencyPosition || 'AFTER');
      })
      .catch(() => !cancelled && toast.error(t('systemSettings.fetchError')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [t]);

  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || currency;
  const previewWith = (pos) =>
    formatMoney(PREVIEW_AMOUNT, { currency, currencySymbol: symbol, currencyPosition: pos });

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await systemSettingsService.updateSettings({ currency, currencyPosition: position });
      // Push it into the session so every price in the admin re-renders at once.
      dispatch(setCurrency(saved));
      toast.success(t('systemSettings.saveSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('systemSettings.title')}
      </Typography>

      {!isSuperAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('systemSettings.superAdminOnly')}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t('systemSettings.currencySection')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('systemSettings.currencyHelp')}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={!isSuperAdmin}>
                <InputLabel>{t('systemSettings.currencyLabel')}</InputLabel>
                <Select
                  value={currency}
                  label={t('systemSettings.currencyLabel')}
                  onChange={(e) => setCurrencyCode(e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {t(`systemSettings.currencies.${c.code}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl disabled={!isSuperAdmin}>
                <FormLabel>{t('systemSettings.position')}</FormLabel>
                <RadioGroup value={position} onChange={(e) => setPosition(e.target.value)}>
                  <FormControlLabel
                    value="BEFORE"
                    control={<Radio />}
                    label={`${t('systemSettings.before')} (${t('systemSettings.example', { example: previewWith('BEFORE') })})`}
                  />
                  <FormControlLabel
                    value="AFTER"
                    control={<Radio />}
                    label={`${t('systemSettings.after')} (${t('systemSettings.example', { example: previewWith('AFTER') })})`}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('systemSettings.preview')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {previewWith(position)}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning">{t('systemSettings.currencyWarning')}</Alert>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('systemSettings.futureSettings')}
          </Typography>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !isSuperAdmin}
          >
            {t('common.save')}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemSettings;
